import React, { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import apiClient from '../stateManagers/services/axiosInterceptor';
import { useDefaultDataStore } from '../stateManagers/store/useDefaultDataStore';
import { useSchoolDetailsStore } from '../stateManagers/newStores/useSchoolDetailsStore';
import { useAuthStore } from '../stateManagers/store/authStore';

// ---------------------------------------------------------------------------
// upsertOption — Uses the new Go API: POST /api/user-options
// For superadmin: pass schoolId in payload
// For admin: schoolId is resolved from JWT
// ---------------------------------------------------------------------------
async function upsertOption(name: string, value: any, schoolId?: string): Promise<void> {
  const payload: any = {
    option_name: name,
    option_value: value,
  };
  
  // ✅ If schoolId is provided (superadmin), send it
  if (schoolId) {
    payload.school_id = schoolId;
  }
  
  const res = await apiClient.post('/api/user-options', payload);

  if (res.data?.status !== 'success') {
    throw new Error(res.data?.message || `Failed to save option "${name}"`);
  }
}

interface TimetableType {
  id: number;
  type: string;
}

interface FormData {
  schoolZone: string;
  schoolEmail: string;
  schoolPhone: string;
}

interface SavedFields {
  schoolZone: boolean;
  schoolEmail: boolean;
  schoolPhone: boolean;
  timetableTypes: boolean;
}

const schoolZoneOptions = [
  { value: 'summer', label: 'Summer' },
  { value: 'winter', label: 'Winter' },
];

export default function InitialSetupComponent() {
  const activeSchool = useDefaultDataStore(state => state.active_school);
  const { schoolDetails, fetchSchoolDetails } = useSchoolDetailsStore();
  
  // ✅ Get user role to determine if superadmin
  const { user } = useAuthStore();
  const userRole = (user as any)?.role || '';
  const isSuperAdmin = userRole === 'superadmin';

  // ✅ Get school ID from schoolDetails store
  const schoolId = schoolDetails?.school_id || '';
  const schoolIdCode = schoolDetails?.school_id_code || '';

  // ✅ Fetch school details if not available
  useEffect(() => {
    // Get school ID from activeSchool or localStorage
    const schoolIdFromActive = activeSchool?.school_id || '';
    const storedSchoolId = localStorage.getItem('schoolId') || '';
    const schoolIdToFetch = schoolIdFromActive || storedSchoolId;
    
    if (schoolIdToFetch && !schoolDetails) {
      fetchSchoolDetails(schoolIdToFetch);
    }
  }, [activeSchool, schoolDetails, fetchSchoolDetails]);

  // ✅ Debug log
  useEffect(() => {
    console.log('🔵 School Details:', schoolDetails);
    console.log('🔵 School ID from schoolDetails:', schoolId);
    console.log('🔵 School ID Code from schoolDetails:', schoolIdCode);
    console.log('🔵 User Role:', userRole);
    console.log('🔵 Is SuperAdmin:', isSuperAdmin);
  }, [schoolId, schoolIdCode, userRole, isSuperAdmin, schoolDetails]);

  const [formData, setFormData] = useState<FormData>({
    schoolZone: '',
    schoolEmail: '',
    schoolPhone: '',
  });

  const [savedFields, setSavedFields] = useState<SavedFields>({
    schoolZone: false,
    schoolEmail: false,
    schoolPhone: false,
    timetableTypes: false,
  });

  const [timetableTypes, setTimetableTypes] = useState<TimetableType[]>([{ id: 1, type: '' }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  
  // Get current session from schoolDetails
  const currentSession = schoolDetails?.current_session?.session || '';
  
  const timetableCounter = useRef(1);
  const toast = useRef<Toast>(null);

  const allSaved = Object.values(savedFields).every(Boolean);

  useEffect(() => {
    fetchExistingData();
  }, [schoolId]);

  const fetchExistingData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Options using the new Go API
      // For superadmin: pass school_id as query param
      let optionsUrl = '/api/user-options/multiple?names=school_zone,school_email,school_phone';
      if (isSuperAdmin && schoolId) {
        optionsUrl += `&school_id=${schoolId}`;
      }
      
      console.log('🔵 Fetching options from:', optionsUrl);
      
      const optionsResponse = await apiClient.get(optionsUrl);
      
      if (optionsResponse.data.status === 'success' && optionsResponse.data.data) {
        const d = optionsResponse.data.data;
        const schoolZone = d.school_zone || '';
        const schoolEmail = d.school_email || '';
        const schoolPhone = d.school_phone || '';

        setFormData({ schoolZone, schoolEmail, schoolPhone });

        setSavedFields((prev) => ({
          ...prev,
          schoolZone: !!schoolZone,
          schoolEmail: !!schoolEmail,
          schoolPhone: !!schoolPhone,
        }));
      }

      // 2. Fetch Timetable Types
      console.log('🔵 Fetching timetable types with schoolId:', schoolId);
      
      const timetableUrl = schoolId 
        ? `/timetable-types?school_id=${schoolId}`
        : '/timetable-types';
      
      console.log('🔵 Timetable URL:', timetableUrl);
      
      const timetableResponse = await apiClient.get(timetableUrl);
      console.log('📥 Timetable response:', timetableResponse.data);
      
      if (timetableResponse.data.status === 'success' && timetableResponse.data.data?.length > 0) {
        const types = timetableResponse.data.data.map((item: any, i: number) => ({
          id: i + 1,
          type: item.name || '',
        }));
        setTimetableTypes(types);
        timetableCounter.current = types.length;
        setSavedFields((prev) => ({ ...prev, timetableTypes: true }));
      }

    } catch (err) {
      console.error('❌ Fetch error:', err);
      toast.current?.show({ severity: 'warn', summary: 'Info', detail: 'Starting fresh setup.', life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (name: string, value: string) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const setSavingKey = (key: string, val: boolean) =>
    setSaving((prev) => ({ ...prev, [key]: val }));

  // ── Individual save handlers ───────────────────────────────────────────────

  const saveSchoolZone = async () => {
    if (!formData.schoolZone) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please select a School Zone.', life: 3000 });
      return;
    }
    setSavingKey('schoolZone', true);
    try {
      // ✅ Pass schoolId from schoolDetails for superadmin
      await upsertOption('school_zone', formData.schoolZone, schoolId);
      setSavedFields((prev) => ({ ...prev, schoolZone: true }));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'School Zone saved.', life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save School Zone.', life: 5000 });
    } finally {
      setSavingKey('schoolZone', false);
    }
  };

  const saveSchoolEmail = async () => {
    if (!formData.schoolEmail.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please enter a School Email.', life: 3000 });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.schoolEmail.trim())) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please enter a valid email address.', life: 3000 });
      return;
    }
    setSavingKey('schoolEmail', true);
    try {
      // ✅ Pass schoolId from schoolDetails for superadmin
      await upsertOption('school_email', formData.schoolEmail.trim(), schoolId);
      setSavedFields((prev) => ({ ...prev, schoolEmail: true }));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'School Email saved.', life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save School Email.', life: 5000 });
    } finally {
      setSavingKey('schoolEmail', false);
    }
  };

  const saveSchoolPhone = async () => {
    if (!formData.schoolPhone.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please enter a School Phone No.', life: 3000 });
      return;
    }
    setSavingKey('schoolPhone', true);
    try {
      // ✅ Pass schoolId from schoolDetails for superadmin
      await upsertOption('school_phone', formData.schoolPhone.trim(), schoolId);
      setSavedFields((prev) => ({ ...prev, schoolPhone: true }));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'School Phone No. saved.', life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save School Phone No.', life: 5000 });
    } finally {
      setSavingKey('schoolPhone', false);
    }
  };

  const saveTimetableTypes = async () => {
    const valid = timetableTypes.filter((t) => t.type.trim() !== '');
    if (valid.length === 0) {
      toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Add at least one timetable type.', life: 3000 });
      return;
    }
    if (!currentSession) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Current session not found. Please ensure a session is marked as "Current" in Session Management.', life: 4000 });
      return;
    }
    setSavingKey('timetableTypes', true);
    try {
      const payload: any = {
        names: valid.map((t) => t.type),
        session: currentSession,
      };
      
      // ✅ Use schoolId from schoolDetails for superadmin
      if (schoolId) {
        payload.school_id = schoolId;
      }
      
      console.log('📤 Saving timetable types payload:', payload);
      
      const res = await apiClient.post('/timetable-types/dynamic-insert', payload);
      console.log('📥 Timetable save response:', res.data);
      
      const timetableResults: any[] = Array.isArray(res.data.results) ? res.data.results : [];
      const errors = timetableResults.filter((r: any) => r.status === 'error');
      
      if (errors.length > 0) {
        errors.forEach((e: any) =>
          toast.current?.show({ severity: 'error', summary: 'Timetable Error', detail: e.message || 'Unknown error.', life: 5000 })
        );
        return;
      }
      
      setSavedFields((prev) => ({ ...prev, timetableTypes: true }));
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Timetable Types saved.', life: 3000 });
    } catch (err: any) {
      console.error('❌ Save timetable error:', err);
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'Failed to save Timetable Types.', life: 5000 });
    } finally {
      setSavingKey('timetableTypes', false);
    }
  };

  const handleDeleteValue = async (field: string) => {
    try {
      // ✅ Pass schoolId from schoolDetails for superadmin
      await upsertOption(field, '', schoolId);
      setSavedFields((prev) => ({ ...prev, [field]: false }));
      
      if (field === 'school_zone') setFormData(prev => ({ ...prev, schoolZone: '' }));
      if (field === 'school_email') setFormData(prev => ({ ...prev, schoolEmail: '' }));
      if (field === 'school_phone') setFormData(prev => ({ ...prev, schoolPhone: '' }));
      
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `${field} removed.`, life: 3000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete.', life: 3000 });
    }
  };

  const handleFlushData = async () => {
    toast.current?.show({ severity: 'info', summary: 'Coming Soon', detail: 'Flush will be implemented with the delete API.', life: 3000 });
  };

  const handleEditField = (field: keyof SavedFields) =>
    setSavedFields((prev) => ({ ...prev, [field]: false }));

  // Timetable helpers
  const addTimetableType = () => {
    timetableCounter.current += 1;
    setTimetableTypes((prev) => [...prev, { id: timetableCounter.current, type: '' }]);
  };
  
  const removeTimetableType = (id: number) => {
    if (timetableTypes.length <= 1) {
      toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'At least one timetable type is required.', life: 3000 });
      return;
    }
    setTimetableTypes((prev) => prev.filter((t) => t.id !== id));
  };
  
  const handleTimetableChange = (id: number, value: string) =>
    setTimetableTypes((prev) => prev.map((t) => (t.id === id ? { ...t, type: value } : t)));

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#fdfdfd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '48px', color: '#3b82f6' }}></i>
          <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '16px' }}>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
      <Toast ref={toast} />

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
        borderRadius: '24px', padding: '32px', marginBottom: '24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '16px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-cog" style={{ fontSize: '18px', color: 'white' }}></i>
          </div>
          <div>
            <h1 style={{ color: 'white', fontSize: '28px', fontWeight: '600', margin: '0 0 4px 0' }}>Initial Setup</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                background: allSaved ? 'rgba(74,222,128,0.9)' : 'rgba(251,191,36,0.9)',
                color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600',
              }}>
                {allSaved ? 'Fully Configured' : 'In Progress'}
              </span>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                {Object.values(savedFields).filter(Boolean).length} / {Object.values(savedFields).length} sections saved
              </span>
            </div>
          </div>
        </div>
        {allSaved && (
          <button
            onClick={handleFlushData}
            style={{ background: 'rgba(239,68,68,0.85)', border: 'none', borderRadius: '10px', padding: '10px 18px', cursor: 'pointer', color: 'white', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
            onMouseOver={(e) => { e.currentTarget.style.background = '#ef4444'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.85)'; }}
          >
            <i className="pi pi-trash" style={{ fontSize: '12px' }}></i>
            Flush All
          </button>
        )}
      </div>

      {/* Cards Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>

        {/* School Zone */}
        <SectionCard
          icon="pi-map-marker"
          title="School Zone"
          subtitle="Select your region"
          isSaved={savedFields.schoolZone}
          isSaving={!!saving.schoolZone}
          onEdit={() => handleEditField('schoolZone')}
          onSave={saveSchoolZone}
          savedContent={<SavedTag icon="pi-map-marker" label="School Zone" value={formData.schoolZone} onDelete={() => handleDeleteValue('school_zone')} capitalize />}
        >
          <Dropdown
            value={formData.schoolZone}
            onChange={(e) => handleFormChange('schoolZone', e.value)}
            options={schoolZoneOptions}
            optionLabel="label"
            placeholder="Choose zone"
            style={{ width: '100%', border: '1px solid #f1f5f9', borderRadius: '12px' }}
          />
        </SectionCard>

        {/* School Email */}
        <SectionCard
          icon="pi-envelope"
          title="School Email"
          subtitle="Official contact email"
          isSaved={savedFields.schoolEmail}
          isSaving={!!saving.schoolEmail}
          onEdit={() => handleEditField('schoolEmail')}
          onSave={saveSchoolEmail}
          savedContent={<SavedTag icon="pi-envelope" label="School Email" value={formData.schoolEmail} onDelete={() => handleDeleteValue('school_email')} />}
        >
          <InputText
            value={formData.schoolEmail}
            onChange={(e) => handleFormChange('schoolEmail', e.target.value)}
            placeholder="e.g., admin@school.edu"
            style={{ width: '100%', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '10px 12px' }}
          />
        </SectionCard>

        {/* School Phone No */}
        <SectionCard
          icon="pi-phone"
          title="School Phone No"
          subtitle="Official contact number"
          isSaved={savedFields.schoolPhone}
          isSaving={!!saving.schoolPhone}
          onEdit={() => handleEditField('schoolPhone')}
          onSave={saveSchoolPhone}
          savedContent={<SavedTag icon="pi-phone" label="School Phone" value={formData.schoolPhone} onDelete={() => handleDeleteValue('school_phone')} />}
        >
          <InputText
            value={formData.schoolPhone}
            onChange={(e) => handleFormChange('schoolPhone', e.target.value)}
            placeholder="e.g., +91 98765 43210"
            style={{ width: '100%', border: '1px solid #f1f5f9', borderRadius: '12px', padding: '10px 12px' }}
          />
        </SectionCard>

        {/* Timetable Types */}
        <SectionCard
          icon="pi-clock"
          title="Timetable Types"
          subtitle="Add schedule categories"
          isSaved={savedFields.timetableTypes}
          isSaving={!!saving.timetableTypes}
          onEdit={() => handleEditField('timetableTypes')}
          onSave={saveTimetableTypes}
          gridSpan={3}
          headerExtra={
            !savedFields.timetableTypes ? (
              <button
                onClick={addTimetableType}
                style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', borderRadius: '12px', padding: '10px 20px', cursor: 'pointer', color: 'white', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <i className="pi pi-plus" style={{ fontSize: '14px' }}></i> Add Type
              </button>
            ) : null
          }
          savedContent={
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {timetableTypes.filter((t) => t.type.trim() !== '').map((item) => (
                <SavedTag key={item.id} icon="pi-clock" label="Timetable Type" value={item.type} onDelete={() => {}} />
              ))}
            </div>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {timetableTypes.map((item, index) => (
              <div key={item.id} style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', border: '1px solid #f1f5f9', position: 'relative' }}>
                {timetableTypes.length > 1 && (
                  <button
                    onClick={() => removeTimetableType(item.id)}
                    style={{ position: 'absolute', top: '8px', right: '12px', background: '#ef4444', border: 'none', borderRadius: '8px', width: '22px', height: '22px', cursor: 'pointer', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    <i className="pi pi-times" style={{ fontSize: '12px' }}></i>
                  </button>
                )}
                <label style={{ display: 'block', color: '#2855ae', fontSize: '12px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Type {index + 1}
                </label>
                <InputText
                  value={item.type}
                  onChange={(e) => handleTimetableChange(item.id, e.target.value)}
                  placeholder="e.g., Regular, Exam"
                  style={{ width: '100%', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '10px 12px' }}
                />
              </div>
            ))}
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

// ─── SectionCard Component ──────────────────────────────────────────────────

interface SectionCardProps {
  icon: string;
  title: string;
  subtitle: string;
  isSaved: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onSave: () => void;
  gridSpan?: number;
  headerExtra?: React.ReactNode;
  savedContent: React.ReactNode;
  children: React.ReactNode;
}

function SectionCard({ icon, title, subtitle, isSaved, isSaving, onEdit, onSave, gridSpan, headerExtra, savedContent, children }: SectionCardProps) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '20px',
      padding: '28px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      border: isSaved ? '2px solid #4ade80' : '2px solid transparent',
      gridColumn: gridSpan ? `span ${gridSpan}` : undefined,
      transition: 'border-color 0.3s',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            background: isSaved
              ? 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
              : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            borderRadius: '16px', padding: '12px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.3s',
          }}>
            <i className={`pi ${isSaved ? 'pi-check' : icon}`} style={{ fontSize: '18px', color: 'white' }}></i>
          </div>
          <div>
            <h3 style={{ color: isSaved ? '#22c55e' : '#2855ae', fontSize: '18px', fontWeight: '600', margin: '0 0 4px 0', transition: 'color 0.3s' }}>{title}</h3>
            <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>{subtitle}</p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {headerExtra}
          {isSaved ? (
            <button
              onClick={onEdit}
              style={{ background: 'transparent', border: '1.5px solid #d1d5db', borderRadius: '10px', padding: '8px 16px', cursor: 'pointer', color: '#6b7280', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s' }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <i className="pi pi-pencil" style={{ fontSize: '12px' }}></i>
              Edit
            </button>
          ) : (
            <button
              onClick={onSave}
              disabled={isSaving}
              style={{ background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)', border: 'none', borderRadius: '10px', padding: '8px 18px', cursor: isSaving ? 'not-allowed' : 'pointer', color: 'white', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', opacity: isSaving ? 0.7 : 1, transition: 'all 0.2s' }}
            >
              {isSaving
                ? <><i className="pi pi-spin pi-spinner" style={{ fontSize: '12px' }}></i> Saving...</>
                : <><i className="pi pi-check" style={{ fontSize: '12px' }}></i> Save</>}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {isSaved ? (
        <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '16px', border: '1px solid #bbf7d0' }}>
          {savedContent}
        </div>
      ) : children}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function SavedTag({ icon, label, value, onDelete, capitalize }: {
  icon: string; label: string; value: string; onDelete: () => void; capitalize?: boolean;
}) {
  return (
    <div style={{ background: 'white', borderRadius: '8px', padding: '10px 14px', border: '1px solid #bbf7d0', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
      <i className={`pi ${icon}`} style={{ fontSize: '14px', color: '#22c55e' }}></i>
      <div>
        <span style={{ color: '#166534', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'block', marginBottom: '3px' }}>{label}</span>
        <span style={{ color: '#15803d', fontSize: '14px', fontWeight: '600', textTransform: capitalize ? 'capitalize' : 'none' }}>{value}</span>
      </div>
      <button
        onClick={onDelete}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px', transition: 'all 0.2s', marginLeft: '4px' }}
        onMouseOver={(e) => { e.currentTarget.style.background = '#ef4444'; const ic = e.currentTarget.querySelector('i'); if (ic) (ic as HTMLElement).style.color = 'white'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; const ic = e.currentTarget.querySelector('i'); if (ic) (ic as HTMLElement).style.color = '#ef4444'; }}
      >
        <i className="pi pi-trash" style={{ fontSize: '13px', color: '#ef4444', transition: 'color 0.2s' }}></i>
      </button>
    </div>
  );
}